/**
 * Warehouses admin API klient pro SPA.
 */

const BASE = "/api/admin/warehouses";

export interface WarehouseOverview {
  id: string;
  code: string;
  name: string;
  kind: string;
  country: string | null;
  currency: string;
  priceKind: string;
  active: boolean;
  feedUrl: string | null;
  feedFormat: string | null;
  lastSyncAt: string | null;
  notes: string | null;
  stats: {
    totalSku: number;
    direct: number;
    viaReplacement: number;
    unmatched: number;
    ignored: number;
    matchRatePct: number | null;
  };
  lastRun: WarehouseSyncRun | null;
}

export interface WarehouseSyncRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: string;              // 'running' | 'success' | 'error' | 'aborted_suspicious'
  trigger: string;             // 'manual' | 'cron' | 'api'
  rowsTotal: number | null;
  rowsWithStock: number | null;
  rowsMatched: number | null;
  rowsViaRepl?: number | null;
  rowsOrphaned: number | null;
  rowsDeactivated?: number | null;
  errorMessage: string | null;
}

export interface OrphanRow {
  id: string;
  supplierCode: string;
  name: string | null;
  price: string | null;
  currency: string;
  qtyOnHand: number;
  qtyAvailable: number;
  qtyInbound: number;
  matchStatus: string;
  syncedAt: string;
}

export interface OrphanListResponse {
  total: number;
  limit: number;
  offset: number;
  items: OrphanRow[];
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${url}`);
  return res.json();
}

export async function listWarehouses(): Promise<{ warehouses: WarehouseOverview[] }> {
  return getJson(`${BASE}`);
}

export async function getRuns(
  code: string,
  limit = 50,
): Promise<{ runs: WarehouseSyncRun[] }> {
  return getJson(`${BASE}/${encodeURIComponent(code)}/runs?limit=${limit}`);
}

export async function getOrphans(
  code: string,
  params: { limit?: number; offset?: number; q?: string; status?: string; onlyStock?: boolean } = {},
): Promise<OrphanListResponse> {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.onlyStock) qs.set("onlyStock", "true");
  return getJson(`${BASE}/${encodeURIComponent(code)}/orphans?${qs.toString()}`);
}

export async function triggerSync(code: string): Promise<{ status: string; code: string }> {
  const res = await fetch(`${BASE}/${encodeURIComponent(code)}/sync`, {
    method: "POST",
    credentials: "include",
  });
  if (res.status === 409) {
    const body = await res.json();
    throw new Error(`Sync už běží (runId: ${body.runId ?? "?"})`);
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function patchStock(
  code: string,
  stockId: string,
  action: "assign" | "ignore" | "reset",
  articleNumber?: string,
): Promise<{ ok: boolean; matchStatus: string }> {
  const res = await fetch(`${BASE}/${encodeURIComponent(code)}/stock/${stockId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, articleNumber }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}
